/**
 * Mode-aware primary credit selector — single source of truth for "what number to display/charge".
 *
 * Replaces dual-write pattern (estimatedCredits + accurateTokenCount) post US-11-09 refactor.
 * All UI badges, sum logic, and confirm modals MUST go through this selector to ensure
 * mode-correct totals (translate vs train chain) and null-safe display ("Chưa đếm").
 */

import type { TranslationFile } from '../types';

export type CreditMode = 'translate' | 'train' | 'qc' | 'audit';

type EstimatedCredits = NonNullable<TranslationFile['estimatedCredits']>;

/**
 * Resolve the primary credit number to display/charge for a given mode.
 *
 * - translate → bundle.translate
 * - train     → bundle.translate + (semanticMatch ?? 0) + (audit ?? 0)
 * - qc        → bundle.qc
 * - audit     → bundle.audit
 *
 * Returns null when bundle is missing (file pre-preflight) — caller must
 * render "Chưa đếm" placeholder, NOT 0.
 */
export function selectPrimaryCredits(
  bundle: EstimatedCredits | null | undefined,
  mode: CreditMode,
): number | null {
  if (!bundle) return null;
  switch (mode) {
    case 'translate':
      return typeof bundle.translate === 'number' ? bundle.translate : null;
    case 'train':
      if (typeof bundle.translate !== 'number') return null;
      return bundle.translate + (bundle.semanticMatch ?? 0) + (bundle.audit ?? 0);
    case 'qc':
      return typeof bundle.qc === 'number' ? bundle.qc : null;
    case 'audit':
      return typeof bundle.audit === 'number' ? bundle.audit : null;
  }
}

/**
 * Sum primary credits across multiple files. Skips null entries (files
 * pre-preflight). Returns total of resolved credits — NOT count of files.
 */
export function sumPrimaryCredits(
  bundles: Array<EstimatedCredits | null | undefined>,
  mode: CreditMode,
): number {
  return bundles.reduce<number>((sum, b) => {
    const c = selectPrimaryCredits(b, mode);
    return c == null ? sum : sum + c;
  }, 0);
}
